import styled from '@emotion/styled'

import { metrics } from '../../../__styling/settings/metrics'
import { colors } from '../../../__styling/settings/colors'
import { zIndex } from '../../../__styling/settings/z-index'
import { breakpoint } from '../../../__styling/style-functions'
import { calcRem } from '../../../__styling/style-functions/calc-rem'
import { supports } from '../../../__helpers/at-supports-helper'

import { styledListClassName } from '../../../typography/List/List.styled'
import { Sharing } from '../../../buttons/Sharing/Sharing'
import { withClass } from '@news-mono/web-common'
import { fixedPlaylistHeightCompact } from '../../../content/Video/components/VideoPlaylist/VideoPlaylist.styled'
import { stickyVideoClassName } from '../../../publication/ArticleVideo/ArticleVideo.styled'

const gridGapSize = metrics.sevennews.margins.md
const largeGapPadding = 20

export const StyledGrid = styled('div')(
    {
        margin: '0 auto',
        width: '100%',

        display: 'table', // Grid FallBack
        tableLayout: 'fixed', // Required for IE so content does not overflow
        marginBottom: metrics.sevennews.margins.md,
        borderBottom: `1px solid ${colors.sevennews.alto}`,

        // If grid is supported use css grid
    },
    supports('display: grid', {
        display: 'grid', // Override display: table
        gridTemplateColumns: '100%',
        gridGap: calcRem(gridGapSize),

        '> .row': {
            display: 'table-cell',
        },
    }),
)

export const StyledCell = styled('div')<{ largeGap?: boolean }>(
    {
        display: 'block',
    },
    ({ largeGap }) => ({
        padding: `0 0 ${
            largeGap
                ? calcRem(gridGapSize + largeGapPadding)
                : calcRem(gridGapSize)
        } 0`,
    }),
    ({ largeGap }) =>
        supports('display: grid', {
            padding:
                largeGap === true ? `0 0 ${calcRem(largeGapPadding)} 0` : 0,
        }),
)

export const StyledVideoCell = styled('div')<{ cellHeight?: number }>({
    '--stickyContainerHeight': 0,
    minHeight: `var(--stickyContainerHeight)`,
})

export const StyledNestedGrid = styled('div')({
    display: 'grid',
    gridTemplateColumns: '100%',
    gridGap: calcRem(gridGapSize),

    [breakpoint('md')]: {
        gridTemplateColumns: '40px auto',
    },
    '@media print': {
        display: 'block',
    },
})

export const StyledAside = withClass('hide-print')(
    styled('aside')(
        {
            marginBottom: calcRem(metrics.sevennews.margins.md),
        },
        supports('display: grid', {
            marginBottom: 'unset',
        }),
    ),
)
export const StyledSharing = styled(Sharing)({
    position: 'sticky',
    top: metrics.sevennews.margins.md,
})

export const StyledArticleContent = styled('div')({
    /*
            - Adds the horizontal padding to the article content
            - Excludes the figure component so we can center images & video of unknown width
              but also have the image asset full bleed to the edge on desktop.
        */
    width: '100%',
    gridColumn: 2,

    '& *:last-child': {
        marginBottom: 0,
    },

    // If the last item in an article is an image then remove the border on the figcaption so it doesn't double up with topics
    '& figure:last-child figcaption': {
        borderBottom: 'none',
    },

    // Fix for SWM-4447
    '& .twin-image-wrapper': {
        marginBottom: calcRem(metrics.sevennews.margins.md),

        [breakpoint('sm')]: {
            marginBottom: 0,
        },
    },

    [`& .${styledListClassName}`]: {
        paddingLeft: calcRem(metrics.sevennews.margins.lg),
    },
})

const condensedStickyVideoWidth = 321
const condensedStickyVideoHeight = 180 + fixedPlaylistHeightCompact + 30 // Caption height

export const StyledStickyCell = styled('div')<{ largeGap?: boolean }>(
    {
        display: 'block',

        [`&.${stickyVideoClassName}`]: {
            left: `calc(50vw - ((100vw - 100%)/2) + ${
                condensedStickyVideoWidth / 2
            }px)`,
            position: 'fixed',
            top: `calc(100vh - ${condensedStickyVideoHeight}px)`,
            width: condensedStickyVideoWidth,
            zIndex: zIndex.common.stickyVideo,
            pointerEvents: 'auto',
        },
    },
    ({ largeGap }) => ({
        padding: `0 0 ${
            largeGap
                ? calcRem(gridGapSize + largeGapPadding)
                : calcRem(gridGapSize)
        } 0`,
    }),
    ({ largeGap }) =>
        supports('display: grid', {
            padding:
                largeGap === true ? `0 0 ${calcRem(largeGapPadding)} 0` : 0,
        }),
)
StyledStickyCell.displayName = 'StickyContainer'

export const StyledStickyTop = styled('div')({
    height: 1,
})
StyledStickyTop.displayName = 'StickyWatcher'
